import * as d3 from 'd3';

import i18n from '../../../i18n/config';
import { getComponentSubtypeDisplayLabel } from './assetListFilterLabels';

/**
 * A few component SVGs (backend `ComponentType.svg_*` blobs and the bundled
 * `public/images/machine-graphics/*.svg`) embed a raw `<title>` holding the subtype slug
 * (e.g. `disc_check_valve`). Browsers render that as a native hover tooltip, always in
 * English. These helpers replace it with the localized component label and keep it in
 * sync with the active language.
 *
 * They deliberately only *replace* a title the asset already ships — they never inject
 * one. Most graphics carry no `<title>`, and adding one would give every component a
 * native browser tooltip on top of the custom `InlineTooltip` already rendered for it.
 */

export const COMPONENT_SUBTYPE_ATTR = 'data-component-subtype';

/**
 * Removes only the node's OWN `<title>` children. Deliberately not `selectAll('title')`:
 * these are backend-controlled assets, and a per-part accessibility title added
 * server-side later must not be silently destroyed.
 */
export const stripOwnTitles = (node) => {
  if (!node || !node.childNodes) return;
  Array.prototype.slice.call(node.childNodes)
    .filter(child => child.nodeName === 'title')
    .forEach(child => node.removeChild(child));
};

/** True when the node itself has at least one `<title>` child (nested ones don't count). */
export const hasOwnTitle = (node) => {
  if (!node || !node.childNodes) return false;
  return Array.prototype.slice.call(node.childNodes)
    .some(child => child.nodeName === 'title');
};

/** Writes the localized label for `compType` into the node's own `<title>`. */
export const setLocalizedComponentTitle = (node, compType) => {
  if (!node) return;
  const label = getComponentSubtypeDisplayLabel(i18n.t.bind(i18n), compType);
  if (!label) return;
  const selection = d3.select(node);
  const existing = selection.select('title');
  if (existing.empty()) selection.insert('title', ':first-child').text(label);
  else existing.text(label);
};

/**
 * Tags the node with its subtype and localizes its existing title in one step.
 * No-op when the asset ships no `<title>` — see the module docstring: injecting one
 * would add a second, native tooltip to components that only ever had `InlineTooltip`.
 */
export const applyLocalizedComponentTitle = (node, compType) => {
  if (!node || !hasOwnTitle(node)) return;
  stripOwnTitles(node);
  node.setAttribute(COMPONENT_SUBTYPE_ATTR, compType);
  setLocalizedComponentTitle(node, compType);
};

/**
 * Re-resolves every already-injected title inside `containerNode`. The label is baked
 * into the SVG at injection time, so it does not follow a language change on its own.
 */
export const relabelComponentTitles = (containerNode) => {
  if (!containerNode) return;
  d3.select(containerNode)
    .selectAll(`[${COMPONENT_SUBTYPE_ATTR}]`)
    .each((d, i, nodes) => {
      setLocalizedComponentTitle(nodes[i], nodes[i].getAttribute(COMPONENT_SUBTYPE_ATTR));
    });
};

/**
 * Subscribes `handler` to the events that should trigger a relabel and returns an
 * unsubscribe fn. `loaded` covers a cached SVG injected before the translation bundle
 * arrives (hard refresh with a cold i18n backend).
 */
export const onLocaleChange = (handler) => {
  i18n.on('languageChanged', handler);
  i18n.on('loaded', handler);
  return () => {
    i18n.off('languageChanged', handler);
    i18n.off('loaded', handler);
  };
};
